import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { SERVICE_CATEGORIES } from "@/data/services";
import { SERVICE_IMAGES } from "@/data/serviceImages";

const ServicesPage = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "fr";

  return (
    <>
      <Seo titleKey="seo.services_title" descKey="seo.services_desc" path="/services" />
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-emerald-50/40">
        <div className="container-x pt-14 pb-10 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-blue">{t("nav.services")}</p>
            <h1 className="mt-2 font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-slate-900">
              {t("services_overview.title")}
            </h1>
            <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed">
              {t("services_overview.subtitle")}
            </p>
          </div>
          <div className="hidden lg:block lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden ring-1 ring-slate-200 shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=1200&fit=crop"
                alt="Nettoyage professionnel BIONET SOLUTIONS"
                className="w-full h-64 object-cover"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="container-x pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {SERVICE_CATEGORIES.map((c) => {
            const Icon = c.icon;
            const items = c.items[lang] || c.items.fr;
            return (
              <div
                key={c.slug}
                className="rounded-3xl bg-white border border-slate-200 overflow-hidden ring-soft flex flex-col"
                data-testid={`service-section-${c.slug}`}
              >
                {SERVICE_IMAGES[c.slug] && (
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={SERVICE_IMAGES[c.slug]}
                      alt={t(`categories.${c.slug}.name`)}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      loading="lazy"
                      onError={(e) => { e.target.style.visibility = "hidden"; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent" />
                  </div>
                )}
                <div className="p-6 sm:p-8 flex flex-col flex-1">
                <div className="flex items-start gap-4">
                  <div className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${c.accent}`}>
                    <Icon className="h-6 w-6 text-brand-green" />
                  </div>
                  <div className="flex-1">
                    <h2 className="font-heading font-bold text-2xl text-slate-900">
                      {t(`categories.${c.slug}.name`)}
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">{t(`categories.${c.slug}.tagline`)}</p>
                  </div>
                </div>
                <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-1.5 text-sm text-slate-600">
                  {items.map((it) => (
                    <li key={it} className="flex items-start gap-2">
                      <span className="mt-1.5 h-1 w-1 rounded-full bg-brand-blue" /> {it}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 flex gap-3">
                  <Link to={`/services/${c.slug}`} className="btn-secondary !py-2 !px-4 text-sm">
                    {t("services_overview.cta_more")}
                  </Link>
                  <Link to="/devis" className="btn-primary !py-2 !px-4 text-sm">
                    {t("common.cta_quote_short")} <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
};

export default ServicesPage;
